/**
 * MediaCard - Display component for media items with video scrubbing
 * Converted from TSX to JSX for college_recruiter_v1
 *
 * Features:
 * - DSLR-style grid overlay (5x5)
 * - Video scrubbing with start point selection
 * - Drag handle indicator
 * - Remove button
 * - Time/frame formatting
 *
 * @module v2/analyzer/MediaCard
 */

import React, { useState, useRef } from 'react';
import { MediaType } from '../../../lib/types';
import { FileVideoIcon, FileSpreadsheetIcon, XIcon } from './Icons';

/**
 * DSLR-style grid overlay component - 5x5 grid
 * @param {Object} props
 * @param {boolean} [props.visible=true] - Whether grid is visible
 */
const GridOverlay = ({ visible = true }) => {
  if (!visible) return null;

  return (
    <div className="absolute inset-0 pointer-events-none z-[5]">
      {/* Vertical lines - 5x5 grid (4 lines at 20%, 40%, 60%, 80%) */}
      <div className="absolute left-[20%] top-0 bottom-0 w-px bg-white/25" />
      <div className="absolute left-[40%] top-0 bottom-0 w-px bg-white/25" />
      <div className="absolute left-[60%] top-0 bottom-0 w-px bg-white/25" />
      <div className="absolute left-[80%] top-0 bottom-0 w-px bg-white/25" />
      {/* Horizontal lines - 5x5 grid (4 lines at 20%, 40%, 60%, 80%) */}
      <div className="absolute top-[20%] left-0 right-0 h-px bg-white/25" />
      <div className="absolute top-[40%] left-0 right-0 h-px bg-white/25" />
      <div className="absolute top-[60%] left-0 right-0 h-px bg-white/25" />
      <div className="absolute top-[80%] left-0 right-0 h-px bg-white/25" />
      {/* Center crosshair */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-px bg-white/50" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-4 bg-white/50" />
    </div>
  );
};

/**
 * Drag handle icon component - six dots in grid
 * @param {Object} props
 * @param {string} [props.className] - CSS class name
 */
const DragHandleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="9" cy="5" r="1" fill="currentColor" />
    <circle cx="9" cy="12" r="1" fill="currentColor" />
    <circle cx="9" cy="19" r="1" fill="currentColor" />
    <circle cx="15" cy="5" r="1" fill="currentColor" />
    <circle cx="15" cy="12" r="1" fill="currentColor" />
    <circle cx="15" cy="19" r="1" fill="currentColor" />
  </svg>
);

/**
 * MediaCard Component - Displays a media item with controls
 *
 * @param {Object} props
 * @param {Object} props.item - The media item to display
 * @param {string} props.item.id - Unique identifier
 * @param {string} props.item.name - Display name
 * @param {string} props.item.type - Media type (VIDEO, IMAGE, CSV)
 * @param {string} props.item.previewUrl - URL for preview
 * @param {Function} props.onRemove - Callback when remove is clicked (id) => void
 * @param {Function} [props.onVideoRef] - Callback to get video element ref (id, el) => void
 * @param {boolean} [props.showGrid=true] - Whether to show grid overlay
 * @param {number} [props.startPoint=0] - Start point in seconds for video
 * @param {Function} [props.onStartPointChange] - Callback when start point changes (id, seconds) => void
 */
export const MediaCard = ({
  item,
  onRemove,
  onVideoRef,
  showGrid = true,
  startPoint = 0,
  onStartPointChange
}) => {
  const isVideo = item.type === MediaType.VIDEO;
  const isImage = item.type === MediaType.IMAGE;
  const isCSV = item.type === MediaType.CSV;

  // Local video ref for scrubbing
  const localVideoRef = useRef(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubPosition, setScrubPosition] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [showScrubHint, setShowScrubHint] = useState(false);

  /**
   * Sync local ref with parent ref
   * @param {HTMLVideoElement|null} el - Video element
   */
  const handleVideoRef = (el) => {
    localVideoRef.current = el;
    if (onVideoRef) onVideoRef(item.id, el);

    if (el) {
      // Get duration when metadata loads
      const handleMetadata = () => {
        setVideoDuration(el.duration);
        // Don't auto-set scrub position - let it be controlled by parent
      };
      el.addEventListener('loadedmetadata', handleMetadata);
      if (el.duration) handleMetadata();
    }
  };

  /**
   * Handle scrubber drag start - only updates LOCAL preview
   * @param {React.MouseEvent|React.TouchEvent} e - Event object
   */
  const handleScrubStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsScrubbing(true);
    setShowScrubHint(true);
  };

  /**
   * Handle scrubber drag move
   * @param {React.MouseEvent|React.TouchEvent} e - Event object
   */
  const handleScrubMove = (e) => {
    if (!isScrubbing || !localVideoRef.current || !videoDuration) return;

    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const newTime = x * videoDuration;

    setScrubPosition(newTime);
    // Preview the frame locally
    localVideoRef.current.currentTime = newTime;
  };

  /**
   * Handle scrubber drag end
   * @param {React.MouseEvent|React.TouchEvent} e - Event object
   */
  const handleScrubEnd = (e) => {
    if (!isScrubbing) return;
    e.stopPropagation();
    e.preventDefault();
    setIsScrubbing(false);

    // Commit the start point to parent
    if (onStartPointChange) {
      onStartPointChange(item.id, scrubPosition);
    }

    // Hide hint after a delay
    setTimeout(() => setShowScrubHint(false), 1500);
  };

  /**
   * Format time display
   * @param {number} seconds - Time in seconds
   * @returns {string} Formatted time string
   */
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0.00s';
    return `${seconds.toFixed(2)}s`;
  };

  /**
   * Format as frame number (assuming 30fps)
   * @param {number} seconds - Time in seconds
   * @returns {string} Frame number string
   */
  const formatFrame = (seconds) => {
    if (!seconds || isNaN(seconds)) return 'Frame 0';
    return `Frame ${Math.round(seconds * 30)}`;
  };

  return (
    <div className="group relative flex flex-col items-center justify-center p-1 rounded-xl bg-card border border-border shadow-lg transition-all hover:border-primary/50 hover:shadow-xl w-full h-full min-h-[160px] overflow-hidden cursor-grab active:cursor-grabbing select-none">

      {/* Drag Handle Indicator - visible on touch devices, hover on desktop */}
      <div className="absolute top-2 left-2 z-10 p-1.5 bg-black/60 text-white/70 rounded-lg opacity-50 sm:opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <DragHandleIcon className="w-4 h-4" />
      </div>

      {/* Remove Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(item.id);
        }}
        className="absolute top-2 right-2 z-10 p-1 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/80"
      >
        <XIcon className="w-4 h-4" />
      </button>

      {/* Content Preview */}
      <div className="flex-1 w-full h-full flex items-center justify-center overflow-hidden rounded-lg bg-black">
        {isImage && (
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={item.previewUrl}
              alt={item.name}
              className="w-full h-full object-contain"
            />
            <GridOverlay visible={showGrid} />
          </div>
        )}
        {isVideo && (
          <div className="relative w-full h-full flex items-center justify-center bg-black">
            <video
              ref={handleVideoRef}
              src={item.previewUrl}
              className="w-full h-full object-contain"
              muted
              playsInline
            />
            <GridOverlay visible={showGrid} />
            <div className="absolute top-2 left-2 pointer-events-none">
                <FileVideoIcon className="w-6 h-6 text-white/50 drop-shadow-md" />
            </div>

            {/* Start Point Badge - shows when start point is set */}
            {startPoint > 0 && (
              <div className="absolute top-2 right-8 z-10 px-2 py-1 bg-primary text-primary-foreground text-[9px] font-bold rounded-lg shadow-lg flex items-center gap-1.5">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polygon points="5 3 19 12 5 21 5 3" fill="currentColor" />
                </svg>
                START: {formatTime(startPoint)}
              </div>
            )}

            {/* Scrubbing Status Overlay */}
            {isScrubbing && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center pointer-events-none z-20">
                <div className="px-4 py-3 bg-primary/90 rounded-xl shadow-2xl flex flex-col items-center">
                  <span className="text-primary-foreground text-lg font-bold">{formatTime(scrubPosition)}</span>
                  <span className="text-primary-foreground/70 text-[10px]">{formatFrame(scrubPosition)}</span>
                </div>
              </div>
            )}

            {/* Scrubber Overlay - visible on hover */}
            <div
              className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-2 pt-8 transition-opacity ${isScrubbing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
              onMouseDown={handleScrubStart}
              onMouseMove={handleScrubMove}
              onMouseUp={handleScrubEnd}
              onMouseLeave={handleScrubEnd}
              onTouchStart={handleScrubStart}
              onTouchMove={handleScrubMove}
              onTouchEnd={handleScrubEnd}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: isScrubbing ? 'grabbing' : 'pointer' }}
            >
              {/* Instructions */}
              <div className="text-[9px] text-white/70 text-center mb-2 font-medium">
                {isScrubbing ? (
                  <span className="text-primary">Release to set start point</span>
                ) : startPoint > 0 ? (
                  <span>Drag to change start point</span>
                ) : (
                  <span>Drag to set where this video starts</span>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Scrub Track */}
                <div className="flex-1 h-2 bg-white/20 rounded-full relative overflow-hidden">
                  {/* Start point marker (if set and not scrubbing) */}
                  {startPoint > 0 && !isScrubbing && (
                    <div
                      className="absolute top-0 bottom-0 w-1 bg-primary z-10"
                      style={{ left: `${videoDuration ? (startPoint / videoDuration) * 100 : 0}%` }}
                    />
                  )}
                  {/* Current scrub fill */}
                  <div
                    className={`absolute left-0 top-0 h-full rounded-full transition-all ${isScrubbing ? 'bg-primary' : 'bg-white/40'}`}
                    style={{ width: `${videoDuration ? (scrubPosition / videoDuration) * 100 : 0}%` }}
                  />
                  {/* Scrub handle */}
                  <div
                    className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-primary rounded-full shadow-lg border-2 border-white transition-transform z-20 ${isScrubbing ? 'scale-125 shadow-primary/50' : 'hover:scale-110'}`}
                    style={{ left: `calc(${videoDuration ? (scrubPosition / videoDuration) * 100 : 0}% - 8px)` }}
                  />
                </div>
                {/* Time display */}
                <span className={`text-[10px] font-mono min-w-[42px] text-right font-bold ${isScrubbing ? 'text-primary' : 'text-white/80'}`}>
                  {formatTime(scrubPosition)}
                </span>
              </div>
            </div>
          </div>
        )}
        {isCSV && (
           <div className="flex flex-col items-center text-primary">
             <FileSpreadsheetIcon className="w-12 h-12 mb-2" />
             <span className="text-xs text-muted-foreground px-2 text-center break-all max-w-[120px]">{item.name}</span>
           </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="w-full bg-muted/50 p-2 mt-1 rounded-b-lg">
        <p className="text-[10px] font-medium text-muted-foreground truncate text-center">
            {item.name}
        </p>
      </div>
    </div>
  );
};

export default MediaCard;
